import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
    Alert,
    Box,
    CircularProgress,
    Collapse,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { getCategoryDisplayName } from '../../components/AthleteDetailView';
import ApiClient from '../../config/ApiClient';
import { FadedPaper } from '../../theme/StyledFields';
import { formatFullDate } from '../../utils/notificationUtils';

type SurveySubmissionRow = {
    id: number;
    athlete_id: number;
    athlete_name: string;
    respondent_id: number;
    respondent_name: string;
    respondent_type: string;
    lesson_plan_id: number;
    session_number: number;
    survey_type: string;
    excels_in_domains: string[];
    growth_opportunities_domains: string[] | null;
    progress_changes: string;
    feedback_suggestions: string;
    satisfaction_rating: number | null;
    lasting_changes: string | null;
    submitted_at: string | null;
};

type SurveyReportResponse = {
    count: number;
    limit: number;
    offset: number;
    results: SurveySubmissionRow[];
};

const DEFAULT_LIMIT = 50;
const LIMIT_OPTIONS = [25, 50, 100];

const getFieldsWithData = (row: SurveySubmissionRow): string => {
    const fields: string[] = [];
    if (row.progress_changes?.trim()) fields.push('Progress Changes');
    if (row.feedback_suggestions?.trim()) fields.push('Feedback Suggestions');
    if (row.satisfaction_rating != null) fields.push('Satisfaction');
    if (row.lasting_changes?.trim()) fields.push('Lasting Changes');
    return fields.length ? fields.join(', ') : '—';
};

const SurveySubmissionsSection: React.FC = () => {
    const [rows, setRows] = useState<SurveySubmissionRow[]>([]);
    const [count, setCount] = useState(0);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

    const page = useMemo(() => Math.floor(offset / limit), [offset, limit]);

    useEffect(() => {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
            limit: String(limit),
            offset: String(offset),
        });

        ApiClient.get<SurveyReportResponse>(`/api/reports/survey-submissions/?${params.toString()}`)
            .then((res) => {
                if (!res.success || !res.data) {
                    setError(res.error || 'Failed to load survey submissions');
                    return;
                }
                setRows(res.data.results || []);
                setCount(res.data.count || 0);
                setOffset(res.data.offset || 0);
                setLimit(res.data.limit || limit);
            })
            .catch(() => {
                setError('Failed to load survey submissions');
            })
            .finally(() => setLoading(false));
    }, [offset, limit]);

    const handleChangePage = (_: unknown, newPage: number) => {
        setOffset(newPage * limit);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextLimit = parseInt(event.target.value, 10) || DEFAULT_LIMIT;
        setLimit(nextLimit);
        setOffset(0);
    };

    const handleToggleExpandedRow = (rowId: number) => {
        setExpandedRowId((current) => (current === rowId ? null : rowId));
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ mb: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box data-testid="section-survey-submissions">
            <FadedPaper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Survey submissions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Flat submission list ordered by athlete ID and submission time.
                </Typography>
            </FadedPaper>

            <FadedPaper sx={{ p: 0, mb: 3 }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Athlete</TableCell>
                                <TableCell>Respondent</TableCell>
                                <TableCell>Session</TableCell>
                                <TableCell>Submitted</TableCell>
                                <TableCell>Excels In</TableCell>
                                <TableCell>Growth Opportunities</TableCell>
                                <TableCell>Text Responses</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => {
                                const isExpanded = expandedRowId === row.id;
                                return (
                                    <React.Fragment key={row.id}>
                                        <TableRow>
                                            <TableCell>{row.athlete_name}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 100 }}>{row.respondent_type}</Typography>
                                                {row.respondent_name}</TableCell>
                                            <TableCell>{row.session_number}</TableCell>
                                            <TableCell>{row.submitted_at ? formatFullDate(row.submitted_at) : '—'}</TableCell>
                                            <TableCell>{row.excels_in_domains?.map(domain => getCategoryDisplayName(domain)).join(', ') || '—'}</TableCell>
                                            <TableCell>{row.growth_opportunities_domains?.map(domain => getCategoryDisplayName(domain)).join(', ') || '—'}</TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    aria-label={isExpanded ? 'Collapse survey details' : 'Expand survey details'}
                                                    onClick={() => handleToggleExpandedRow(row.id)}
                                                >
                                                    {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                </IconButton>
                                                {' '}{getFieldsWithData(row)}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ py: 0 }}>
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ p: 2, pt: 1 }}>
                                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                                            <strong>Progress Changes:</strong> {row.progress_changes || '—'}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                                            <strong>Feedback Suggestions:</strong> {row.feedback_suggestions || '—'}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                                            <strong>Satisfaction:</strong> {row.satisfaction_rating ?? '—'}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            <strong>Lasting Changes:</strong> {row.lasting_changes || '—'}
                                                        </Typography>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        No survey submissions found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={count}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={LIMIT_OPTIONS}
                />
            </FadedPaper>
        </Box>
    );
};

export default SurveySubmissionsSection;
